import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowLeft, Share2, BookOpen, Tag, Eye, ChevronRight, ArrowRight, FileText } from 'lucide-react';
import Layout from '../layouts/Layout';
import apiClient from '../helpers/apiClient';

const NewsDetailPage = () => {
    const { slug } = useParams();
    const contentRef = useRef(null);
    const [article, setArticle] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (slug) {
            loadArticle();
        }
        // eslint-disable-next-line
    }, [slug]);

    useEffect(() => {
    const timeout = setTimeout(() => {
        if (contentRef.current) {
            const links = contentRef.current.querySelectorAll('a[href^="http"]');
            links.forEach((link) => {
                const isExternal = !link.href.includes(window.location.hostname);
                if (isExternal) {
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                }
            });
        }
    }, 2000); // Wait for DOM update after dangerouslySetInnerHTML

    return () => clearTimeout(timeout);
}, [article?.content]);

    const loadArticle = async () => {
        try {
            setLoading(true);
            setError('');

            // Load the article
            const response = await apiClient.getNewsArticle(slug);

            if (response.success) {
                setArticle(response.data);

                // Load related articles
                if (response.data.category) {
                    loadRelatedArticles(response.data.category, response.data.id);
                }
            } else {
                setError('Article not found');
            }
        } catch (err) {
            console.error('Error loading article:', err);
            setError('Failed to load article');
        } finally {
            setLoading(false);
        }
    };

    const loadRelatedArticles = async (category, excludeId) => {
        try {
            const response = await apiClient.getNewsByCategory(category, 4);
            if (response.success) {
                // Filter out the current article
                const filtered = response.data.filter(a => a.id !== excludeId);
                setRelatedArticles(filtered.slice(0, 3));
            }
        } catch (err) {
            console.error('Error loading related articles:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return '';
        }
    };

    const getReadingTime = (readingTime) => {
        if (!readingTime) return '';
        return `${readingTime} min read`;
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Technology': 'bg-blue-100 text-blue-800',
            'Strategy': 'bg-purple-100 text-purple-800',
            'Compliance': 'bg-red-100 text-red-800',
            'Design': 'bg-green-100 text-green-800',
            'Development': 'bg-indigo-100 text-indigo-800',
            'News': 'bg-gray-100 text-gray-800',
            'Tutorial': 'bg-orange-100 text-orange-800',
            'Case Study': 'bg-teal-100 text-teal-800',
            'Industry': 'bg-yellow-100 text-yellow-800'
        };
        return colors[category] || 'bg-slate-100 text-slate-800';
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: article.title,
                    text: article.excerpt,
                    url: window.location.href,
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(window.location.href);
            // You could show a toast notification here
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="min-h-screen bg-white">
                    <div className="max-w-4xl mx-auto px-6 py-32">
                        <div className="animate-pulse">
                            <div className="h-8 bg-slate-200 rounded w-1/4 mb-8"></div>
                            <div className="h-12 bg-slate-200 rounded w-3/4 mb-4"></div>
                            <div className="h-6 bg-slate-200 rounded w-1/2 mb-8"></div>
                            <div className="h-64 bg-slate-200 rounded mb-8"></div>
                            <div className="space-y-4">
                                <div className="h-4 bg-slate-200 rounded"></div>
                                <div className="h-4 bg-slate-200 rounded"></div>
                                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error || !article) {
        return (
            <Layout title="Article Not Found">
                <div className="min-h-screen bg-white flex items-center justify-center">
                    <div className="text-center px-6">
                        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-slate-900 mb-2">Article Not Found</h1>
                        <p className="text-slate-600 mb-6">
                            The article you're looking for doesn't exist or has been removed.
                        </p>
                        <Link
                            to="/news"
                            className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to News
                        </Link>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            title={article.meta_title || article.title}
            description={article.meta_description || article.excerpt}
            keywords={article.tags ? article.tags.join(', ') : ''}
            path={`/news/${article.slug}`}
        >
            <article className="min-h-screen bg-white">
                {/* Breadcrumb */}
                <div className="border-b border-slate-100">
                    <div className="max-w-4xl mx-auto px-6 py-6 pt-24">
                        <nav className="flex items-center text-sm text-slate-600">
                            <Link to="/" className="hover:text-slate-900 transition-colors">
                                Home
                            </Link>
                            <ChevronRight className="w-4 h-4 mx-2" />
                            <Link to="/news" className="hover:text-slate-900 transition-colors">
                                News
                            </Link>
                            <ChevronRight className="w-4 h-4 mx-2" />
                            <span className="text-slate-900 truncate">{article.title}</span>
                        </nav>
                    </div>
                </div>

                {/* Article Header */}
                <header className="max-w-4xl mx-auto px-6 py-12">
                    <div className="mb-8">
                        <Link
                            to="/news"
                            className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors mb-8"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to News
                        </Link>

                        {/* Article Meta */}
                        <div className="flex flex-wrap items-center gap-4 mb-6">
                            {article.category && (
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(article.category)}`}>
                                    {article.category}
                                </span>
                            )}
                            <div className="flex items-center text-slate-500">
                                <Calendar className="w-4 h-4 mr-1" />
                                <span className="text-sm">
                                    {formatDate(article.published_at || article.created_at)}
                                </span>
                            </div>
                            {article.reading_time && (
                                <div className="flex items-center text-slate-500">
                                    <Clock className="w-4 h-4 mr-1" />
                                    <span className="text-sm">{getReadingTime(article.reading_time)}</span>
                                </div>
                            )}
                            {article.views !== undefined && (
                                <div className="flex items-center text-slate-500">
                                    <Eye className="w-4 h-4 mr-1" />
                                    <span className="text-sm">{article.views} views</span>
                                </div>
                            )}
                        </div>

                        {/* Title */}
                        <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                            {article.title}
                        </h1>

                        {/* Excerpt */}
                        {article.excerpt && (
                            <p className="text-xl text-slate-600 leading-relaxed mb-8">
                                {article.excerpt}
                            </p>
                        )}

                        {/* Author and Share */}
                        <div className="flex items-center justify-between border-t border-b border-slate-100 py-6">
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center mr-4">
                                    <User className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <div className="font-medium text-slate-900">
                                        {article.author || 'KGC Innovations'}
                                    </div>
                                    <div className="text-sm text-slate-500">Author</div>
                                </div>
                            </div>

                            <button
                                onClick={handleShare}
                                className="flex items-center px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                            >
                                <Share2 className="w-4 h-4 mr-2" />
                                Share
                            </button>
                        </div>
                    </div>

                    {/* Featured Image */}
                    {article.featured_image && (
                        <div className="mb-12">
                            <img
                                src={article.featured_image}
                                alt={article.title}
                                className="w-full h-96 object-cover rounded-2xl"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                    )}
                </header>

                {/* Article Content */}
                <div className="max-w-4xl mx-auto px-6">
                    <div className="prose prose-lg prose-slate max-w-none">
                        <div
                            ref={contentRef}
                            dangerouslySetInnerHTML={{ __html: article.content }}
                            className="article-content"
                        />
                    </div>

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                        <div className="mt-12 pt-8 border-t border-slate-100 mb-3">
                            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                                <Tag className="w-5 h-5 mr-2" />
                                Tags
                            </h3>
                            <div className="flex flex-wrap gap-3">
                                {article.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                    <section className="max-w-6xl mx-auto px-6 py-16 mt-14 border-t border-slate-100">
                        <h2 className="text-3xl font-bold text-slate-900 mb-12 flex items-center">
                            <BookOpen className="w-8 h-8 mr-3" />
                            Related Articles
                        </h2>

                        <div className="grid md:grid-cols-3 gap-8">
                            {relatedArticles.map((relatedArticle) => (
                                <Link
                                    key={relatedArticle.id}
                                    to={`/news/${relatedArticle.slug}`}
                                    className="group"
                                >
                                    <article className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                        <div className="h-48 bg-slate-100 overflow-hidden">
                                            {relatedArticle.featured_image ? (
                                                <img
                                                    src={relatedArticle.featured_image}
                                                    alt={relatedArticle.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className={`w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center ${relatedArticle.featured_image ? 'hidden' : ''}`}>
                                                <FileText className="w-12 h-12 text-slate-400" />
                                            </div>
                                        </div>

                                        <div className="p-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                {relatedArticle.category && (
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(relatedArticle.category)}`}>
                                                        {relatedArticle.category}
                                                    </span>
                                                )}
                                                <div className="flex items-center text-slate-500 text-sm">
                                                    <Calendar className="w-4 h-4 mr-1" />
                                                    {formatDate(relatedArticle.published_at || relatedArticle.created_at)}
                                                </div>
                                            </div>

                                            <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors line-clamp-2">
                                                {relatedArticle.title}
                                            </h3>

                                            {relatedArticle.excerpt && (
                                                <p className="text-slate-600 mb-4 line-clamp-2">
                                                    {relatedArticle.excerpt}
                                                </p>
                                            )}

                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4 text-slate-500 text-sm">
                                                    {relatedArticle.reading_time && (
                                                        <div className="flex items-center">
                                                            <Clock className="w-4 h-4 mr-1" />
                                                            {getReadingTime(relatedArticle.reading_time)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center text-slate-600 group-hover:text-slate-900 transition-colors">
                                                    <span className="font-medium">Read more</span>
                                                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                </Link>
                            ))}
                        </div>

                        <div className="text-center mt-12">
                            <Link
                                to="/news"
                                className="inline-flex items-center bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg"
                            >
                                View All Articles <ArrowRight className="ml-2 w-5 h-5" />
                            </Link>
                        </div>
                    </section>
                )}
            </article>

            <style jsx>{`
        .article-content h1,
        .article-content h2,
        .article-content h3,
        .article-content h4,
        .article-content h5,
        .article-content h6 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 600;
          color: #0f172a;
        }
        
        .article-content h1 { font-size: 2rem; }
        .article-content h2 { font-size: 1.75rem; }
        .article-content h3 { font-size: 1.5rem; }
        .article-content h4 { font-size: 1.25rem; }
        
        .article-content p {
          margin-bottom: 1.5rem;
          line-height: 1.7;
          color: #475569;
        }
        
        .article-content ul,
        .article-content ol {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        
        .article-content li {
          margin-bottom: 0.5rem;
          color: #475569;
        }
        
        .article-content blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1.5rem;
          margin: 2rem 0;
          font-style: italic;
          color: #64748b;
        }
        
        .article-content code {
          background-color: #f1f5f9;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          color: #0f172a;
        }
        
        .article-content pre {
          background-color: #0f172a;
          color: #f8fafc;
          padding: 1.5rem;
          border-radius: 0.75rem;
          overflow-x: auto;
          margin: 2rem 0;
        }
        
        .article-content a {
          color: #0f172a;
          text-decoration: underline;
        }
        
        .article-content a:hover {
          color: #475569;
        }
      `}</style>
        </Layout>
    );
};

export default NewsDetailPage;